import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { SearchProps } from 'app/interfaces';
import { Notification } from 'app/models';
import { EXPORT_TIMEOUT_MESSAGE } from 'app/constants';
import { MyCoverageService } from 'app/services';
import { ExportFilenameService } from 'app/components';
import * as actions from 'redux/actions';

export abstract class ReportTableActions {
  public isExportingExcel = false;
  public isExportingText = false;
  public selectedExportValue: string = null;
  public exportOptions: any[] = [
    {
      label: 'Export to Excel',
      value: 'excel',
      icon: 'pi pi-fw pi-file-excel',
    },
    {
      label: 'Export to Text',
      value: 'text',
      icon: 'pi pi-fw pi-file',
    },
  ];

  protected abstract projectId: number;
  protected abstract coverageReportRequest: SearchProps;

  constructor(
    private reportExportService: MyCoverageService,
    private reportExportStore: Store<AppState>,
    private exportFilenameService: ExportFilenameService
  ) {}

  public handleExport(event: any) {
    const exportType = event.value;
    const extension = exportType === 'excel' ? 'xls' : 'txt';

    this.exportFilenameService
      .prompt(this.getReportExportFilename(extension))
      .subscribe((filename) => {
        if (!filename) {
          this.resetExportState();
          return;
        }

        this.selectedExportValue = exportType;
        if (exportType === 'excel') {
          this.isExportingExcel = true;
          this.handleExportToExcel(filename);
        } else if (exportType === 'text') {
          this.isExportingText = true;
          this.handleExportToText(filename);
        }
      });
  }

  public openReportMissingDataModal() {
    this.reportExportStore.dispatch(actions.showReportDialog());
  }

  protected getReportExportName(): string {
    return null;
  }

  protected getReportExportFilename(extension: 'xls' | 'txt'): string {
    const reportName = this.exportFilenameService.sanitizeFilename(
      this.getReportExportName()
    );
    if (reportName) {
      return `${reportName}.${extension}`;
    }

    const date = new Date();
    const formattedDate = `${date.getFullYear()}${String(
      date.getMonth() + 1
    ).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;

    return `TechNews_Report_${formattedDate}.${extension}`;
  }

  private handleExportToExcel(filename: string) {
    this.reportExportService
      .coverageReportExportToExcel(this.projectId, this.getExportBody())
      .subscribe({
        next: (response: Blob) => {
          if (response && response.size > 0) {
            const blob = new Blob([response], {
              type: 'application/vnd.ms-excel',
            });
            this.downloadFile(blob, filename);
          } else {
            this.reportExportStore.dispatch(
              actions.showNotification({
                notify: new Notification('No data received from server', 'error', 'tr'),
              })
            );
          }
          this.resetExportState();
        },
        error: (error) => {
          this.resetExportState();
          this.reportExportStore.dispatch(
            actions.showNotification({
              notify: new Notification(
                error?.name === 'TimeoutError' ? EXPORT_TIMEOUT_MESSAGE : 'Error exporting to Excel', 'error', 'tr'),
            })
          );
        },
      });
  }

  private handleExportToText(filename: string) {
    this.reportExportService
      .coverageReportExportToText(this.projectId, this.getExportBody())
      .subscribe({
        next: (response: string) => {
          const blob = new Blob([response], {
            type: 'text/plain;charset=utf-8',
          });
          this.downloadFile(blob, filename);
          this.resetExportState();
        },
        error: (error) => {
          this.resetExportState();
          this.reportExportStore.dispatch(
            actions.showNotification({
              notify: new Notification(
                error?.name === 'TimeoutError' ? EXPORT_TIMEOUT_MESSAGE : 'Error exporting to text', 'error', 'tr'
              ),
            })
          );
        },
      });
  }

  private getExportBody(): SearchProps {
    return this.coverageReportRequest || { timeframe: 'ONE_YEAR' };
  }

  private downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  private resetExportState() {
    this.isExportingExcel = false;
    this.isExportingText = false;
    this.selectedExportValue = null;
  }
}
